<template>
  <div class="mc-shell" :class="{ 'mc-shell--solo': !$slots.sidebar }">
    <div class="mc-shell-row">
      <aside
        v-if="$slots.sidebar"
        class="mc-sidebar-slot"
        :class="{ 'mc-sidebar-slot--nobar': !$slots.bar }"
      >
        <slot name="sidebar" />
      </aside>
      <div class="mc-content-col">
        <div class="mc-appbar-slot"><slot name="appbar" /></div>
        <div v-if="$slots.rail" class="mc-rail-slot"><slot name="rail" /></div>
        <main
          ref="main"
          class="mc-main"
          :class="{ 'mc-main--nobar': !$slots.bar }"
        >
          <div class="mc-screen"><slot /></div>
        </main>
      </div>
    </div>
    <!-- spans the full shell width (not just the content column) so its white
         background reads as one continuous floor with the sidebar card above,
         instead of a seam where sidebar meets a differently-coloured gap -->
    <div v-if="$slots.bar" class="mc-bar-slot"><slot name="bar" /></div>
    <slot name="sheet" />
    <slot name="toast" />
  </div>
</template>

<script>
// Mounts inside SubmitClaim.vue's existing `.submit-claim-shell` card, which
// already provides the centered/full-bleed responsive behaviour (see its
// `@media (max-width: 640px)` rule) — this component only owns the internal
// sidebar/app-bar/rail/scroll-body/bottom-bar layout.
//
// Below 900px: single column (app bar, horizontal rail, body, bottom bar).
// At 900px+: the horizontal rail is replaced by a persistent left sidebar
// (see MotorClaimForm's `sidebar` slot / CaseFileRail) — both the `rail` and
// `sidebar` slot content are always rendered, toggled by media query, matching
// the existing app's convention (see ChoiceScreen.vue's mobile/desktop split).
export default {
  name: 'Shell',
  methods: {
    scrollToTop() {
      if (this.$refs.main) this.$refs.main.scrollTop = 0
    },
  },
}
</script>

<style scoped>
.mc-shell {
  height: calc(100vh - 48px);
  height: calc(100dvh - 48px);
  min-height: 680px;
  position: relative;
  overflow: hidden;
  background: #f7f7fc;
}

.mc-shell-row {
  display: flex;
  height: 100%;
}

.mc-sidebar-slot {
  display: none;
}

.mc-content-col {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.mc-appbar-slot {
  flex: 0 0 auto;
}

.mc-rail-slot {
  flex: 0 0 auto;
}

.mc-main {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 20px 20px 118px;
}

.mc-main--nobar {
  padding-bottom: 28px;
}

.mc-screen {
  animation: mc-slidein 0.26s cubic-bezier(0.22, 0.7, 0.3, 1);
}

/* mobile stays exactly as the design has it (full width); the tablet tier
   (no sidebar yet) caps the readable column so fields don't stretch edge to
   edge */
@media (min-width: 641px) {
  .mc-screen {
    max-width: 680px;
    margin: 0 auto;
  }
}

/* 900px+: the sidebar already anchors the layout, so widen the column
   noticeably (not unbounded — a single input at 1400px would look just as
   broken as the original complaint) so chip rows, option cards and the doc
   grid get more columns instead of leaving space empty next to the sidebar.
   Individual steps pair up their genuinely short fields (reg/phone,
   station/OB, witness name/contact) instead of stretching a single input
   full width — see each step's own <style>. */
@media (min-width: 900px) {
  .mc-screen {
    max-width: 920px;
    /* the 641px rule's margin:0 auto centers this within .mc-main's padded
       box, which can be wider than 920px — that leaves a few px of
       asymmetric centering that throws off pixel-exact alignment with
       .mc-bar-inner (which sits flush left, see BottomBar.vue). Flush left
       here too so both edges match exactly. */
    margin: 0;
  }
}

/* No sidebar (voice path): nothing anchors the left edge, so centre rather than clear a 300px rail. */
@media (min-width: 900px) {
  .mc-shell--solo .mc-screen {
    margin: 0 auto;
  }

  .mc-shell--solo ::v-deep .mc-bar {
    padding-left: 20px;
  }

  .mc-shell--solo ::v-deep .mc-bar-inner {
    margin: 0 auto;
  }
}

@keyframes mc-slidein {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.mc-bar-slot {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 6;
}

@media (max-width: 640px) {
  .mc-shell {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
  }
}

/* 900px+: swap the horizontal rail for the persistent case-file sidebar */
@media (min-width: 900px) {
  .mc-rail-slot {
    display: none;
  }

  .mc-sidebar-slot {
    display: block;
    flex: 0 0 300px;
    height: 100%;
    overflow-y: auto;
  }
}
</style>
